import React from 'react'
import type { FrameOption } from '@renderer/types/configState'
import { blendModeToCss } from '@renderer/utils/filterUtils'
import { thumbnailShowsWholeCanvas } from '@renderer/utils/frameThumbnail'

type FrameDrawImage = FrameOption['drawParams']['images'][number]

export function rotationRadToCssDeg(rotationRad: number | undefined): string | undefined {
  if (rotationRad == null || rotationRad === 0) return undefined
  return `${(rotationRad * 180) / Math.PI}deg`
}

/**
 * An `image` layer placed at canvas coordinates × `scale`: the DOM equivalents of
 * what PrintPreview does on the canvas (rotation about the centre, opacity, blend),
 * so the live strip and the printed one agree. Undefined leaves the CSS defaults
 * (opacity 1, mix-blend-mode normal).
 */
export function frameImageLayerStyle(layer: FrameDrawImage, scale: number): React.CSSProperties {
  const rot = rotationRadToCssDeg(layer.rotation)
  return {
    left: `${layer.left * scale}px`,
    top: `${layer.top * scale}px`,
    width: `${layer.width * scale}px`,
    height: `${layer.height * scale}px`,
    objectFit: 'fill',
    transform: rot ? `rotate(${rot})` : undefined,
    transformOrigin: 'center center',
    opacity: layer.opacity != null ? layer.opacity / 100 : undefined,
    mixBlendMode: blendModeToCss(layer.blendMode)
  }
}

/**
 * The frame behind the photo slots of a capture or retake screen. The parent is
 * the canvas at `scale` (canvasWidth × scale by canvasHeight × scale), and the
 * slots are laid over it in canvas coordinates.
 *
 * A whole-frame thumbnail is that canvas, so it is drawn as before. A strip or an
 * uploaded thumbnail (`thumbnailSource`) is not: stretched over the canvas it
 * would put the wrong picture behind the slots. Then the canvas is drawn from
 * drawParams instead — background colour and image layers, what the print is
 * made of.
 */
const FrameCanvasBackdrop: React.FC<{ frame: FrameOption; scale: number }> = ({ frame, scale }) => {
  if (thumbnailShowsWholeCanvas(frame.thumbnailSource)) {
    return (
      <img
        src={frame.thumbnail}
        alt="Frame"
        className="absolute inset-0 w-full h-full object-contain"
        crossOrigin="anonymous"
      />
    )
  }

  return (
    <div
      className="absolute inset-0 pointer-events-none"
      style={{ backgroundColor: frame.drawParams.backgroundColor || '#FFFFFF' }}
    >
      {frame.drawParams.images.map((layer, i) =>
        layer.type === 'image' && layer.src ? (
          <img
            key={`frame-layer-${i}`}
            src={layer.src}
            alt=""
            className="absolute pointer-events-none"
            crossOrigin="anonymous"
            style={frameImageLayerStyle(layer, scale)}
          />
        ) : null
      )}
    </div>
  )
}

export default FrameCanvasBackdrop
